// PWA plumbing: install prompt capture, service-worker registration, new-build detection.
export const isStandalone = () =>
  (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || window.navigator.standalone === true;

export const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

let deferredPrompt = null;
const installListeners = new Set();

export function captureInstallPrompt() {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    installListeners.forEach((l) => l(true));
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installListeners.forEach((l) => l(false));
  });
}

export const canInstall = () => !!deferredPrompt;

export async function promptInstall() {
  if (!deferredPrompt) return false;
  deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice.catch(() => null);
  deferredPrompt = null;
  installListeners.forEach((l) => l(false));
  return choice?.outcome === "accepted";
}

export function onInstallAvailable(fn) {
  installListeners.add(fn);
  return () => installListeners.delete(fn);
}

// The worker only runs on production builds (or when forced with localStorage.sf_sw = "1" for a manual check).
const swEnabled = () => {
  try { return process.env.NODE_ENV === "production" || localStorage.getItem("sf_sw") === "1"; } catch { return false; }
};

export function registerServiceWorker() {
  if (!("serviceWorker" in navigator) || !swEnabled()) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((e) => console.warn("Service worker registration failed", e));
  });
}

// New-build detection: the hashed main bundle in asset-manifest.json changes on every deploy.
let initialMain = null;
async function currentMainBundle() {
  const r = await fetch(`/asset-manifest.json?_=${Date.now()}`, { cache: "no-store" });
  if (!r.ok) throw new Error(String(r.status));
  const j = await r.json();
  return j?.files?.["main.js"] || null;
}

export function startUpdateWatcher(onUpdate, { intervalMs = 5 * 60 * 1000 } = {}) {
  let stopped = false;
  let notified = false;
  const check = async () => {
    if (stopped || notified) return;
    try {
      const main = await currentMainBundle();
      if (!main) return;
      if (initialMain == null) { initialMain = main; return; }
      if (main !== initialMain) { notified = true; onUpdate(); }
    } catch { /* offline or blocked — try again later */ }
  };
  check();
  const iv = setInterval(check, intervalMs);
  const onVisible = () => { if (document.visibilityState === "visible") check(); };
  document.addEventListener("visibilitychange", onVisible);
  return () => { stopped = true; clearInterval(iv); document.removeEventListener("visibilitychange", onVisible); };
}

export async function applyUpdate() {
  try {
    const reg = await navigator.serviceWorker?.getRegistration?.();
    if (reg?.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
    await reg?.update?.();
  } catch { /* ignore */ }
  window.location.reload();
}
